// Import required libraries
import matter from 'gray-matter';

// Import blog post metadata and content
import { blogPostsMetadata, getBlogPostContent } from '../data/blogData';
import type { BlogPostMetadata } from '../data/blogData';

// Re-export the BlogPostMetadata type
export type { BlogPostMetadata };

// Define BlogPost type
export interface BlogPost extends BlogPostMetadata {
  content: string;
}

/**
 * Get all blog posts metadata
 */
export function getAllPostsMetadata(): BlogPostMetadata[] {
  return blogPostsMetadata;
}

/**
 * Get a specific blog post by slug
 */
export async function getPostBySlug(slug: string): Promise<BlogPost | null> {
  // Find the post metadata
  const postMetadata = blogPostsMetadata.find((post) => post.slug === slug);
  
  if (!postMetadata) {
    console.warn(`Post with slug ${slug} not found`);
    return null;
  }
  
  try {
    // Fetch the content using the new getBlogPostContent function
    const rawContent = await getBlogPostContent(slug);
    
    // Parse front matter if needed
    const { content } = matter(rawContent);
    
    // Return the post with content
    return {
      ...postMetadata,
      content
    };
  } catch (error) {
    console.error(`Error fetching content for post with slug ${slug}:`, error);
    return null;
  }
}

/**
 * Get all blog posts with their content
 */
export async function getAllPosts(): Promise<BlogPost[]> {
  // Get all post metadata
  const allPostsMetadata = getAllPostsMetadata();
  
  // Fetch content for each post
  const postsPromises = allPostsMetadata.map(async (metadata) => {
    try {
      const post = await getPostBySlug(metadata.slug);
      return post;
    } catch (error) {
      console.error(`Error fetching post ${metadata.slug}:`, error);
      return null;
    }
  });
  
  // Wait for all promises to resolve and filter out any nulls
  const posts = await Promise.all(postsPromises);
  return posts.filter((post): post is BlogPost => post !== null);
}
